import { motion } from "framer-motion";
import { Globe, ShieldAlert } from "lucide-react";
import { langByCode, getYouTubeId, renderStoryParagraphs } from "../lib/blogHelpers";

/**
 * Renders the *body* of a blog: synopsis, story with interleaved inline media,
 * mini-gallery, original-language flag, AI-translation disclosure, and
 * copyright footer. Used by both BlogDetail and the admin Preview modal.
 *
 * Props:
 *  - blog: full blog object (translations, media, inlineMedia, originalLang, aiTranslatedLangs, authorName, publishedAt)
 *  - activeLang: currently displayed translation code (e.g. "en", "bn", "hi")
 *  - readerMode?: { fromLang: string }  — present when this is an on-demand reader translation; shows extra notice
 */
export default function BlogReader({ blog, activeLang, readerMode = null }) {
  const tr = (blog.translations || {})[activeLang] || {};
  const paragraphs = renderStoryParagraphs(tr.story);

  // Inline media is shared across languages (positions identical for EN/BN)
  const inlineMedia = blog.inlineMedia || [];
  const inlineByAfterPara = new Map();
  inlineMedia.forEach((m) => {
    const k = Number(m.afterParagraph || 0);
    if (!inlineByAfterPara.has(k)) inlineByAfterPara.set(k, []);
    inlineByAfterPara.get(k).push(m);
  });

  const originalMeta = langByCode(blog.originalLang || "en");
  const isAiTranslated =
    !readerMode &&
    activeLang !== (blog.originalLang || "en") &&
    (blog.aiTranslatedLangs || []).includes(activeLang);

  const author = blog.authorName || "Rajib Sengupta";
  const year = blog.publishedAt
    ? new Date(blog.publishedAt).getFullYear()
    : new Date().getFullYear();

  const renderInlineGroup = (items, keyPrefix) => (
    <div className="my-8 space-y-8" data-testid={`inline-media-group-${keyPrefix}`}>
      {items.map((m) => (
        <InlineMediaTile key={m.id || `${keyPrefix}-${m.url}`} m={m} />
      ))}
    </div>
  );

  return (
    <div className="blog-reader" data-testid="blog-reader">
      {/* Original-language flag */}
      <div className="container-narrow max-w-3xl">
        <div className="flex flex-wrap items-center gap-2 -mt-2 mb-2">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] text-[11px] tracking-[0.14em] uppercase text-[#5C5852]"
            data-testid="blog-original-lang-flag"
          >
            <Globe size={11} strokeWidth={1.8} />
            Original: {originalMeta.native}
          </span>
          {readerMode && (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-[#CC7A52]/10 border border-[#CC7A52]/40 text-[11px] tracking-[0.14em] uppercase text-[#CC7A52]"
              data-testid="blog-reader-translation-flag"
            >
              Live machine translation — for personal reading only
            </span>
          )}
        </div>
      </div>

      {/* Synopsis */}
      <section className="pt-2">
        <div className="container-narrow max-w-3xl">
          <motion.p
            key={activeLang + "-synopsis"}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-xl sm:text-2xl font-serif italic text-[#2C2A28] leading-relaxed border-l-2 border-[#CC7A52] pl-5"
            data-testid="blog-synopsis"
          >
            {tr.synopsis}
          </motion.p>
        </div>
      </section>

      {/* Story with interleaved inline media */}
      <section className="py-10 sm:py-14">
        <div className="container-narrow max-w-3xl">
          <motion.div
            key={activeLang + "-story"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            data-testid="blog-story"
          >
            {/* Inline media at position 0 (before first paragraph) */}
            {inlineByAfterPara.has(0) && renderInlineGroup(inlineByAfterPara.get(0), "0")}

            {paragraphs.map((p, i) => {
              const oneBased = i + 1;
              const after = inlineByAfterPara.get(oneBased);
              return (
                <div key={`p-${i}`}>
                  <p className="text-[#2C2A28] leading-relaxed text-lg mb-6 whitespace-pre-line">
                    {p}
                  </p>
                  {after && renderInlineGroup(after, String(oneBased))}
                </div>
              );
            })}
          </motion.div>

          {/* AI translation disclosure */}
          {isAiTranslated && (
            <p
              className="mt-2 text-sm italic text-[#5C5852] border-t border-dashed border-[#D8D3CC] pt-4"
              data-testid="blog-ai-translation-notice"
            >
              <ShieldAlert size={13} className="inline -mt-0.5 mr-1.5 text-[#CC7A52]" />
              This {langByCode(activeLang).native} version has been translated from the original {originalMeta.native} with AI assistance, preserving the original story, style and events.
            </p>
          )}

          {/* Reader-requested live-translation note */}
          {readerMode && (
            <p
              className="mt-2 text-sm italic text-[#5C5852] border-t border-dashed border-[#CC7A52]/40 pt-4"
              data-testid="blog-reader-translation-note"
            >
              <ShieldAlert size={13} className="inline -mt-0.5 mr-1.5 text-[#CC7A52]" />
              Translated by Gemini Flash. If something reads wrong, the original {originalMeta.native} version is always the source of truth.
            </p>
          )}

          {/* Copyright */}
          <p
            className="mt-8 text-xs text-[#5C5852] tracking-wide"
            data-testid="blog-copyright"
          >
            © {year} {author}. Original story, style and events are the sole intellectual
            property of the author. All rights reserved.
          </p>
        </div>
      </section>

      {/* Mini-gallery (up to 10) */}
      {blog.media?.length > 0 && (
        <section className="pb-16">
          <div className="container-narrow">
            <div className="grid sm:grid-cols-2 gap-5" data-testid="blog-media-grid">
              {blog.media.slice(0, 10).map((m, i) => (
                <figure
                  key={`${m.url}-${i}`}
                  className="rounded-sm overflow-hidden bg-[#F2EFEB] border border-[#D8D3CC]"
                  data-testid={`blog-media-${i}`}
                >
                  {m.type === "image" ? (
                    <img
                      src={m.url}
                      alt={m.caption || `Photo ${i + 1}`}
                      loading="lazy"
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                      className="w-full h-auto object-cover no-copy"
                    />
                  ) : (
                    <div className="aspect-video bg-[#2C2A28]">
                      <iframe
                        title={m.caption || `Video ${i + 1}`}
                        src={`https://www.youtube.com/embed/${getYouTubeId(m.url)}`}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                  {m.caption && (
                    <figcaption className="p-4 text-sm text-[#5C5852] italic">
                      {m.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ---------- Inline media tile ---------- */
const SIZE_CLASSES = {
  sm: "max-w-md",
  md: "max-w-2xl",
  lg: "max-w-full",
};

function InlineMediaTile({ m }) {
  const size = SIZE_CLASSES[m.size] || SIZE_CLASSES.md;

  const media = m.type === "image" ? (
    <img
      src={m.url}
      alt={m.caption || ""}
      loading="lazy"
      draggable="false"
      onContextMenu={(e) => e.preventDefault()}
      className="w-full h-auto object-cover no-copy"
    />
  ) : (
    <div className="aspect-video bg-[#2C2A28]">
      <iframe
        title={m.caption || "Embedded video"}
        src={`https://www.youtube.com/embed/${getYouTubeId(m.url)}`}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );

  // Side-by-side: image on left or right, caption text beside; stacks on mobile
  if (m.layout === "side-by-side") {
    const sideRight = m.side === "right";
    return (
      <figure
        className={`grid md:grid-cols-5 gap-5 items-start ${
          sideRight ? "md:[&>.media]:order-2" : ""
        }`}
        data-testid={`inline-media-side-${m.side}`}
      >
        <div className="media md:col-span-3 rounded-sm overflow-hidden bg-[#F2EFEB] border border-[#D8D3CC]">
          {media}
        </div>
        {m.caption ? (
          <figcaption className="md:col-span-2 text-sm text-[#5C5852] italic leading-relaxed md:pt-2">
            {m.caption}
          </figcaption>
        ) : (
          <div className="md:col-span-2" />
        )}
      </figure>
    );
  }

  // Centred
  return (
    <figure
      className={`mx-auto ${size} rounded-sm overflow-hidden bg-[#F2EFEB] border border-[#D8D3CC]`}
      data-testid="inline-media-center"
    >
      {media}
      {m.caption && (
        <figcaption className="px-4 py-3 text-sm text-[#5C5852] italic text-center">
          {m.caption}
        </figcaption>
      )}
    </figure>
  );
}
